import type { AccountEvent, DayViolationSummary, FundedEvent, PayoutEvent } from "./TradingCalendar";

/**
 * Événements de compte affichés par le calendrier (TradingCalendar) et la fenêtre
 * de détail du jour. Partagé par le tableau de bord Advanced et le Lite : les deux
 * calendriers doivent annoncer exactement les mêmes événements pour les mêmes comptes.
 *
 * Les fonctions reçoivent des comptes DÉJÀ filtrés par l'appelant.
 */
type CalendarAccount = {
  name: string;
  createdAt?: string | Date | null;
  disabledAt?: string | Date | null;
  fundedAt?: string | Date | null;
  currency?: string | null;
  payouts?: { date: Date | string; amount: number }[] | null;
  ruleViolations?: Record<string, { types?: string[] }> | null;
};

export function buildAccountEvents(accounts: CalendarAccount[]): AccountEvent[] {
  const events: AccountEvent[] = [];
  for (const account of accounts) {
    events.push({ date: new Date(account.createdAt as string | Date), accountName: account.name, type: "created" });
    if (account.disabledAt) {
      events.push({ date: new Date(account.disabledAt), accountName: account.name, type: "crashed" });
    }
  }
  return events;
}

export function buildFundedEvents(accounts: CalendarAccount[]): FundedEvent[] {
  return accounts
    .filter((a) => a.fundedAt)
    .map((a) => ({ date: new Date(a.fundedAt as string | Date), accountName: a.name }));
}

export function buildPayoutEvents(accounts: CalendarAccount[]): PayoutEvent[] {
  const events: PayoutEvent[] = [];
  for (const account of accounts) {
    for (const p of account.payouts ?? []) {
      events.push({ date: new Date(p.date), accountName: account.name, amount: p.amount, currency: account.currency ?? "USD" });
    }
  }
  return events;
}

export interface ViolationLabels {
  dailyLossViolation: string;
  dailyLossWarning: string;
  consistencyViolation: string;
  consistencyWarning: string;
}

/** Violations de règles par jour ("yyyy-MM-dd"), agrégées sur les comptes reçus. */
export function buildViolationsByDate(accounts: CalendarAccount[], labels: ViolationLabels): Map<string, DayViolationSummary> {
  const map = new Map<string, DayViolationSummary>();
  for (const account of accounts) {
    const violations = account.ruleViolations;
    if (!violations) continue;
    for (const [date, v] of Object.entries(violations)) {
      const types: string[] = v.types ?? [];
      const existing = map.get(date) ?? { severity: "warning" as const, hasDailyLoss: false, hasConsistency: false, tooltips: [] };
      const isViolation = types.some((tp) => tp.endsWith("_violation"));
      if (isViolation || existing.severity === "violation") existing.severity = "violation";
      if (types.some((tp) => tp.startsWith("daily_loss"))) existing.hasDailyLoss = true;
      if (types.some((tp) => tp.startsWith("consistency"))) existing.hasConsistency = true;
      if (types.includes("daily_loss_violation")) existing.tooltips.push(`${account.name}: ${labels.dailyLossViolation}`);
      else if (types.includes("daily_loss_warning")) existing.tooltips.push(`${account.name}: ${labels.dailyLossWarning}`);
      if (types.includes("consistency_violation")) existing.tooltips.push(`${account.name}: ${labels.consistencyViolation}`);
      else if (types.includes("consistency_warning")) existing.tooltips.push(`${account.name}: ${labels.consistencyWarning}`);
      map.set(date, existing);
    }
  }
  return map;
}
